import React from 'react';
import type { PublicPlan } from '@wix/ambassador-pricing-plans-v2-plan/types';
import { VIEWER_HOOKS } from '@wix/pricing-plans-common/hooks';
import type { ButtonDemo } from '../../../../../constants/settings-events';
import { PlanButtonType } from '../../../../../types/common';
import { DisplayedButtons, TopButton } from '../../../stylesParams';
import { Cta } from './Cta';
import { st, classes } from './index.st.css';

export interface PlanButtonsProps {
  plan: PublicPlan;
  highlighted: boolean;
  buttonDemo: ButtonDemo;
  isGoToCheckoutLoading: boolean;
  onBuyNowClick: () => void;
  isAddToCartLoading?: boolean;
  onAddToCartClick: () => void;
  isCartEnabled?: boolean;
  displayedButtons: DisplayedButtons;
  topButton: TopButton;
  primaryButtonAlignment?: string;
  secondaryButtonAlignment?: string;
}

export const PlanButtons = ({
  plan,
  highlighted,
  buttonDemo,
  isGoToCheckoutLoading,
  onBuyNowClick,
  isAddToCartLoading,
  onAddToCartClick,
  isCartEnabled,
  displayedButtons,
  topButton,
  primaryButtonAlignment,
  secondaryButtonAlignment,
}: PlanButtonsProps) => {
  if (!isCartEnabled) {
    return (
      <SingleButton
        plan={plan}
        highlighted={highlighted}
        buttonDemo={buttonDemo}
        primaryButtonAlignment={primaryButtonAlignment}
        loading={isGoToCheckoutLoading}
        onClick={onBuyNowClick}
      />
    );
  }

  if (displayedButtons !== DisplayedButtons.BOTH) {
    const isAddToCart = displayedButtons === DisplayedButtons.ADD_TO_CART;

    return (
      <SingleButton
        plan={plan}
        highlighted={highlighted}
        buttonDemo={buttonDemo}
        primaryButtonAlignment={primaryButtonAlignment}
        isAddToCartText={isAddToCart}
        loading={isAddToCart ? !!isAddToCartLoading : isGoToCheckoutLoading}
        onClick={isAddToCart ? onAddToCartClick : onBuyNowClick}
      />
    );
  }

  return (
    <>
      <div
        data-hook={VIEWER_HOOKS.PLAN_CTA_WRAPPER_PRIMARY}
        className={st(classes.ctaWrapper, {
          buttonAlignment: primaryButtonAlignment,
        })}
      >
        <Cta
          plan={plan}
          highlighted={highlighted}
          buttonDemo={buttonDemo}
          buttonStyle={PlanButtonType.PRIMARY}
          disabled={isGoToCheckoutLoading || isAddToCartLoading}
          loading={(topButton === TopButton.BUY_NOW ? isGoToCheckoutLoading : isAddToCartLoading) ?? false}
          onClick={topButton === TopButton.BUY_NOW ? onBuyNowClick : onAddToCartClick}
          isAddToCartText={topButton === TopButton.ADD_TO_CART}
        />
      </div>
      <div
        data-hook={VIEWER_HOOKS.PLAN_CTA_WRAPPER_SECONDARY}
        className={st(classes.ctaWrapper, {
          buttonAlignment: secondaryButtonAlignment,
          secondary: true,
        })}
      >
        <Cta
          plan={plan}
          highlighted={highlighted}
          disabled={isGoToCheckoutLoading || isAddToCartLoading}
          buttonDemo={buttonDemo}
          buttonStyle={PlanButtonType.SECONDARY}
          loading={(topButton === TopButton.ADD_TO_CART ? isGoToCheckoutLoading : isAddToCartLoading) ?? false}
          onClick={topButton === TopButton.ADD_TO_CART ? onBuyNowClick : onAddToCartClick}
          isAddToCartText={topButton !== TopButton.ADD_TO_CART}
        />
      </div>
    </>
  );
};

const SingleButton = ({
  plan,
  highlighted,
  buttonDemo,
  onClick,
  primaryButtonAlignment,
  isAddToCartText,
  loading,
}: {
  plan: PublicPlan;
  highlighted: boolean;
  buttonDemo: ButtonDemo;
  onClick: () => void;
  primaryButtonAlignment?: string;
  isAddToCartText?: boolean;
  loading: boolean;
}) => {
  return (
    <div
      data-hook={VIEWER_HOOKS.PLAN_CTA_WRAPPER_PRIMARY}
      className={st(classes.ctaWrapper, {
        buttonAlignment: primaryButtonAlignment ?? '',
      })}
    >
      <Cta
        plan={plan}
        highlighted={highlighted}
        buttonDemo={buttonDemo}
        loading={loading}
        buttonStyle={PlanButtonType.PRIMARY}
        onClick={onClick}
        isAddToCartText={isAddToCartText}
      />
    </div>
  );
};
